import type { NearbyOrganizationsResponse } from "./types/nearby-organization";

import { normalizeNanpGeoSearchTerm } from "@homeinstead/shared";
import { mapOfficesToOrganizations } from "./map-offices-to-organizations";
import { searchOffices } from "./search-offices";

type FetchOfficeGeoSearchOptions = {
  onError?: (error: unknown, message: string) => void | Promise<void>;
};

function parseOfficeGeoSearchParams(searchParams: URLSearchParams) {
  const location = searchParams.get("query");
  const forceProximitySearch =
    searchParams.get("forceProximitySearch") === "true";
  const milesParam = searchParams.get("miles");
  const radius =
    milesParam === null ? undefined : Number.parseInt(milesParam, 10);

  return { forceProximitySearch, location, radius };
}

async function fetchOfficeOrganizations(
  searchParams: URLSearchParams,
): Promise<NearbyOrganizationsResponse> {
  const { forceProximitySearch, location, radius } =
    parseOfficeGeoSearchParams(searchParams);

  if (!location) {
    throw new Error("Missing location parameter");
  }

  const officeSearchParams = {
    forceProximitySearch,
    geoSearchTerm: normalizeNanpGeoSearchTerm(location),
    ...(forceProximitySearch ? { mileRadius: radius ?? 30 } : {}),
  };

  const { offices } = await searchOffices(officeSearchParams, {
    throwOnError: true,
  });

  return mapOfficesToOrganizations(offices);
}

async function fetchOfficeGeoSearch(
  request: Request,
  options: FetchOfficeGeoSearchOptions = {},
): Promise<Response> {
  try {
    const data = await fetchOfficeOrganizations(
      new URL(request.url).searchParams,
    );
    return Response.json({ data, success: true });
  } catch (error) {
    if (
      error instanceof Error &&
      error.message === "Missing location parameter"
    ) {
      return Response.json(
        { message: "Missing location parameter", success: false },
        { status: 400 },
      );
    }

    console.error("Error fetching locations:", error);
    const message = `Error fetching locations: ${error}`;
    await options.onError?.(error, message);

    return Response.json(
      {
        message: error instanceof Error ? error.message : "Server error",
        success: false,
      },
      { status: 500 },
    );
  }
}

export { fetchOfficeGeoSearch, fetchOfficeOrganizations };
